import { Link } from 'react-router-dom';
import { BRAND, ORIGINS, type Brand } from '@/brand';

/* Dual-brand footer for the two hub sites (Run & Comply / Grow & Raise).
   Real Conncct logo (white), real route links, cross-brand columns that jump
   to the other domain, and the "separate legal entities" brand framing. */

const LOGO_WHITE = 'https://storage.googleapis.com/connct-cba18.firebasestorage.app/frontend-assets/logon.png';
const INK = '#17263A';
const MUTED = '#AEB9C8';
const CREAM = '#fff';

const isCorp = BRAND === 'corporate';
const HOVER = isCorp ? '#FFD54F' : '#7fc0ff';
const otherBrand: Brand = isCorp ? 'consulting' : 'corporate';
const selfName = isCorp ? 'Run & Comply' : 'Grow & Raise';
const otherName = isCorp ? 'Grow & Raise' : 'Run & Comply';

const CTA_HEADING = isCorp ? 'Ready to raise? Meet Grow & Raise.' : 'Need to set up & stay compliant?';
const CTA_DESC = isCorp
  ? 'Capital readiness & consulting are handled by Grow & Raise — a separate company under the Conncct brand.'
  : 'Company setup, accounting & tax are handled by Run & Comply — a separate company under the Conncct brand.';
const BRAND_NOTE = isCorp
  ? 'UAE company setup, accounting & compliance. Fundraising & consulting are provided by Grow & Raise, a separate company under the Conncct brand.'
  : 'Capital readiness & consulting to help you raise. Company setup, accounting & compliance are provided by Run & Comply, a separate company under the Conncct brand.';

type Item = { label: string; brand: Brand; path: string };
const STAY: Item[] = [
  { label: 'Accounting & Tax', brand: 'corporate', path: '/accounting' },
  { label: 'Corporate Services', brand: 'corporate', path: '/corporate' },
  { label: 'Accounting pricing', brand: 'corporate', path: '/accounting/pricing' },
  { label: 'Corporate pricing', brand: 'corporate', path: '/corporate/pricing' },
];
const GROW: Item[] = [
  { label: 'Consulting', brand: 'consulting', path: '/capital-raising/pricing' },
  { label: 'Capital Readiness', brand: 'consulting', path: '/capital-raising' },
  { label: 'Overview', brand: 'consulting', path: '/' },
];
const COMPANY = [
  { label: 'Home', path: '/' },
  { label: 'About', path: '/about' },
  { label: 'Blog', path: '/blog' },
  { label: 'Careers', path: '/careers' },
  { label: 'FAQs', path: '/faq' },
  { label: 'Contact', path: '/contact' },
  { label: 'Privacy policy', path: '/privacy' },
];

const SOCIALS = [
  { label: 'LinkedIn', href: 'https://www.linkedin.com/company/conncct', path: 'M4.98 3.5a2.5 2.5 0 11-.02 5 2.5 2.5 0 01.02-5zM3 9h4v12H3zM9 9h3.8v1.7h.06c.53-1 1.83-2.06 3.77-2.06C20.4 8.6 21 10.6 21 13.7V21h-4v-6.4c0-1.53-.03-3.5-2.13-3.5-2.14 0-2.47 1.67-2.47 3.4V21H9z' },
  { label: 'X', href: 'https://x.com/conncctadvisory', path: 'M17.5 3h3l-6.6 7.5L21.7 21h-6l-4.3-5.6L6.4 21H3.3l7-8L2.7 3h6.1l3.9 5.1zM16.4 19.1h1.7L7.7 4.8H5.9z' },
];

/* Renders an internal Link (same brand) or a cross-domain <a> (other brand). */
function FLink({ brand, path, children }: { brand: Brand; path: string; children: React.ReactNode }) {
  const cls = 'bf-link';
  if (brand === BRAND) return <Link to={path} className={cls} onClick={() => window.scrollTo(0, 0)}>{children}</Link>;
  return <a href={ORIGINS[brand] + (path === '/' ? '' : path)} className={cls}>{children}</a>;
}

function Col({ title, items }: { title: string; items: Item[] }) {
  return (
    <div>
      <h4 style={{ color: CREAM, fontSize: 13, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 14, fontWeight: 800 }}>{title}</h4>
      {items.map(i => <div key={i.label}><FLink brand={i.brand} path={i.path}>{i.label}</FLink></div>)}
    </div>
  );
}

export default function BrandFooter() {
  return (
    <footer style={{ background: INK, color: MUTED, padding: '60px 0 34px', fontFamily: "'Montserrat', system-ui, sans-serif" }}>
      <style>{`
        .bf-link{display:block;color:${MUTED};font-size:14px;padding:5px 0;text-decoration:none;transition:color .16s;cursor:pointer}
        .bf-link:hover{color:${HOVER}}
        .bf-ctabtn{display:inline-flex;align-items:center;gap:8px;padding:12px 20px;border-radius:999px;font-weight:800;font-size:14px;border:2px solid rgba(255,255,255,.28);color:#fff;text-decoration:none;transition:.16s}
        .bf-ctabtn:hover{background:#fff;color:${INK};border-color:#fff;transform:translateY(-2px)}
        .bf-soc a{width:36px;height:36px;border-radius:50%;border:1px solid rgba(255,255,255,.22);display:grid;place-items:center;color:${MUTED};transition:.16s}
        .bf-soc a:hover{background:rgba(255,255,255,.1);color:#fff;transform:translateY(-2px)}
        .bf-grid{display:grid;grid-template-columns:1.6fr 1fr 1fr 1fr;gap:32px}
        @media(max-width:820px){.bf-grid{grid-template-columns:1fr 1fr}}
      `}</style>

      <div style={{ maxWidth: 1180, margin: '0 auto', padding: '0 24px' }}>
        {/* cross-brand banner */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 22, flexWrap: 'wrap', paddingBottom: 30, marginBottom: 34, borderBottom: '1px solid rgba(255,255,255,.12)' }}>
          <div>
            <h3 style={{ color: CREAM, fontSize: 'clamp(20px,2.4vw,28px)', marginBottom: 5, letterSpacing: '-0.02em', fontWeight: 800 }}>{CTA_HEADING}</h3>
            <p style={{ color: MUTED, fontSize: 14, maxWidth: 430, lineHeight: 1.5 }}>{CTA_DESC}</p>
          </div>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <Link to="/" className="bf-ctabtn" onClick={() => window.scrollTo(0, 0)}>{selfName} →</Link>
            <a href={ORIGINS[otherBrand]} className="bf-ctabtn">{otherName} →</a>
          </div>
        </div>

        {/* columns */}
        <div className="bf-grid">
          <div>
            <Link to="/" onClick={() => window.scrollTo(0, 0)} style={{ display: 'inline-block' }}>
              <img src={LOGO_WHITE} alt="Conncct" style={{ height: 30, width: 'auto', filter: 'brightness(0) invert(1)' }} loading="lazy" />
            </Link>
            <p style={{ fontSize: 14, maxWidth: 300, marginTop: 12, lineHeight: 1.5 }}>{BRAND_NOTE}</p>
            <div className="bf-soc" style={{ display: 'flex', gap: 10, marginTop: 16 }}>
              {SOCIALS.map(s => (
                <a key={s.label} href={s.href} target="_blank" rel="noopener noreferrer" aria-label={s.label}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d={s.path} /></svg>
                </a>
              ))}
              <a href="https://www.instagram.com/conncct.co/" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="5" /><circle cx="12" cy="12" r="4" /><circle cx="17.2" cy="6.8" r="1" fill="currentColor" stroke="none" /></svg>
              </a>
            </div>
          </div>

          {/* own-brand column first, then the other brand */}
          {isCorp
            ? <><Col title="Stay Compliant" items={STAY} /><Col title="Grow & Raise" items={GROW} /></>
            : <><Col title="Grow & Raise" items={GROW} /><Col title="Stay Compliant" items={STAY} /></>}

          <div>
            <h4 style={{ color: CREAM, fontSize: 13, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 14, fontWeight: 800 }}>Company</h4>
            {COMPANY.map(c => <div key={c.label}><Link to={c.path} className="bf-link" onClick={() => window.scrollTo(0, 0)}>{c.label}</Link></div>)}
          </div>
        </div>

        <div style={{ borderTop: '1px solid #243245', marginTop: 40, paddingTop: 20, display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10, fontSize: 13 }}>
          <span>© 2026 Conncct · Built in the UAE. Conncct's accounting &amp; corporate services and its capital-readiness &amp; consulting business are operated by separate legal entities under the Conncct brand.</span>
          <span>Terms · Privacy</span>
        </div>
      </div>
    </footer>
  );
}
